/** @jsx jsx */
/** @jsxRuntime classic */
import { jsx } from '@emotion/react';
import BuildingIcon from './Building';
import Caret from './Caret';
import CheckmarkCircle from './CheckmarkCircle';
import CheckmarkCircleInverted from './CheckmarkCircleInverted';
import ChevronLeftIcon from './ChevronLeft';
import ChevronRightIcon from './ChevronRight';
import ChevronRightThinIcon from './ChevronRightThin';
import ClearIcon from './Clear';
import ConnectorIcon from './Connector';
import DollarIcon from './Dollar';
import { EyeOpenedIcon, EyeClosedIcon } from './Eye';
import PasswordLockIcon from './PasswordLock';
import PinwheelLogo from './PinwheelLogo';
import SearchIcon from './Search';
import XIcon from './X';
import XThinIcon from './XThin';
import ShieldIcon from './Shield';
import CheckmarkOutsideCircleIcon from './CheckmarkOutsideCircle';
import IncognitoIcon from './Incognito';
import LockIcon from './Lock';
import PassInfoIcon from './PassInfo';
import PieChartIcon from './PieChart';
import Step1Icon from './Step1Icon';
import Step2Icon from './Step2Icon';
import Step3Icon from './Step3Icon';
import ThumbsUpIcon from './ThumbsUp';
import GenericPayrollProviderIcon from './GenericPayrollProvider';
import Exclamation from './Exclamation';
import DotsSpinner from './DotsSpinner';
import CopyIcon from './Copy';
import QuestionIcon from './Question';
import ShieldCheckIcon from './ShieldCheck';
import UserSearchIcon from './UserSearch';
import FlashIcon from './Flash';
import CertifiedRibbon from './CertifiedRibbon';
import Search2 from './Search2';
import Lock2Icon from './Lock2';
import FingerprintIcon from './Fingerprint';
import BillCalendar from './BillCalendar';
import BillDetect from './BillDetect';
import BillManage from './BillManage';
import { SxProp } from '../style/sxProp';
import { ColorResolveOptions } from '../style/colors';
import useTheme from '../style/useTheme';

export const ICON_NAMES = [
  'building',
  'caret',
  'clear',
  'checkmarkCircle',
  'checkmarkCircleInverted',
  'checkmarkOutsideCircle',
  'chevronLeft',
  'chevronRight',
  'chevronRightThin',
  'connector',
  'copy',
  'dollar',
  'eyeClosed',
  'eyeOpened',
  'genericPayrollProvider',
  'passwordLock',
  'pinwheelLogo',
  'search',
  'search2',
  'shield',
  'shieldCheck',
  'x',
  'xThin',
  'incognito',
  'lock',
  'lock2',
  'passInfo',
  'pieChart',
  'step1',
  'step2',
  'step3',
  'thumbsUp',
  'exclamation',
  'dotsSpinner',
  'question',
  'userSearch',
  'flash',
  'certifiedRibbon',
  'fingerPrint',
  'billCalendar',
  'billDetect',
  'billManage',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

type CommonIconPropsBase = {
  className?: string;
  viewBox?: string;
};

export type CommonIconProps = CommonIconPropsBase & {
  color?: string;
  backgroundColor?: string;
};

export type Props = CommonIconPropsBase & {
  backgroundColor?: ColorResolveOptions;
  color?: ColorResolveOptions;
  size?: number;
  icon: IconName;
  sx?: SxProp;
};

const ICON_MAP: {
  [K in IconName]: ({ color }: CommonIconProps) => JSX.Element;
} = {
  building: BuildingIcon,
  caret: Caret,
  checkmarkCircle: CheckmarkCircle,
  checkmarkCircleInverted: CheckmarkCircleInverted,
  checkmarkOutsideCircle: CheckmarkOutsideCircleIcon,
  chevronLeft: ChevronLeftIcon,
  chevronRight: ChevronRightIcon,
  chevronRightThin: ChevronRightThinIcon,
  clear: ClearIcon,
  connector: ConnectorIcon,
  copy: CopyIcon,
  dollar: DollarIcon,
  eyeClosed: EyeClosedIcon,
  eyeOpened: EyeOpenedIcon,
  genericPayrollProvider: GenericPayrollProviderIcon,
  passwordLock: PasswordLockIcon,
  pinwheelLogo: PinwheelLogo,
  search: SearchIcon,
  search2: Search2,
  shield: ShieldIcon,
  shieldCheck: ShieldCheckIcon,
  x: XIcon,
  xThin: XThinIcon,
  incognito: IncognitoIcon,
  lock: LockIcon,
  lock2: Lock2Icon,
  passInfo: PassInfoIcon,
  pieChart: PieChartIcon,
  step1: Step1Icon,
  step2: Step2Icon,
  step3: Step3Icon,
  thumbsUp: ThumbsUpIcon,
  exclamation: Exclamation,
  dotsSpinner: DotsSpinner,
  question: QuestionIcon,
  userSearch: UserSearchIcon,
  flash: FlashIcon,
  certifiedRibbon: CertifiedRibbon,
  fingerPrint: FingerprintIcon,
  billCalendar: BillCalendar,
  billDetect: BillDetect,
  billManage: BillManage,
};

const Icon = ({
  size = 24,
  color = {
    type: 'theme',
    colorLegacy: 'Neutral/70',
    colorNext: 'neutral/default',
  },
  backgroundColor = {
    type: 'theme',
    colorLegacy: 'Neutral/0',
    colorNext: 'background/default',
  },
  icon,
  className,
  sx,
  viewBox = `0 0 24 24`,
}: Props): JSX.Element => {
  const { resolveColor } = useTheme();
  const El = ICON_MAP[icon];
  const colorResolved = resolveColor(color);
  const backgroundColorResolved = resolveColor(backgroundColor);
  return (
    <svg
      data-testid={`Icon-${icon}`}
      className={className}
      css={sx}
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...El.prototype.svgPropOverrides}
    >
      <El color={colorResolved} backgroundColor={backgroundColorResolved} />
    </svg>
  );
};

export default Icon;
